import { useEffect, useState } from "react";
import { X, Mail, MessageSquare, Copy, CalendarCheck, Zap, Trash2, Phone, Building2, Sparkles, RefreshCw, PenLine, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { stageMeta } from "../lib/stages";
import StageSelect from "./StageSelect";
import MoneyInput from "./DealValueInput";
import CallsPanel from "./CallsPanel";
import { sendEmail, connectGmail } from "../lib/gmail";

export default function ProspectDrawer({ prospect, onClose, onPatch, onUpdate, onDeleted }) {
  const [notes, setNotes] = useState("");
  const [research, setResearch] = useState(prospect?.research || null);
  const [rStatus, setRStatus] = useState(prospect?.research_status || "none");
  const otypeFor = (stage) =>
    stage === "follow_up_book" ? "book"
      : stage === "follow_up_close" ? "close_deal"
        : stage === "call_scheduled" ? "close"
          : "reach_out";
  const otype = otypeFor(prospect?.stage);
  const [outreach, setOutreach] = useState(prospect?.outreach_copy?.[otype] || null);
  const [oLoading, setOLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  useEffect(() => {
    if (prospect) {
      setNotes(prospect.notes || "");
    }
  }, [prospect?.id]);

  useEffect(() => {
    if (!prospect) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prospect, onClose]);

  useEffect(() => {
    setResearch(prospect?.research || null);
    setRStatus(prospect?.research_status || "none");
    setOutreach(prospect?.outreach_copy?.[otypeFor(prospect?.stage)] || null);
    setOLoading(false);
  }, [prospect?.id]);

  useEffect(() => {
    setEmailSubject(outreach?.subject || "");
    setEmailBody(outreach?.body || "");
  }, [outreach]);

  useEffect(() => {
    if (rStatus !== "pending" || !prospect) return;
    const t = setInterval(async () => {
      const { data } = await api.get("/prospects");
      const me = data.find((x) => x.id === prospect.id);
      if (me) { setResearch(me.research); setRStatus(me.research_status); }
    }, 5000);
    return () => clearInterval(t);
  }, [rStatus, prospect?.id]);

  const runResearch = async () => {
    setRStatus("pending");
    await api.post(`/prospects/${prospect.id}/research`);
  };

  const draftOutreach = async (regenerate = false) => {
    setOLoading(true);
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/outreach`, { type: otype, regenerate });
      setOutreach(data.copy);
      if (regenerate) toast.success("Fresh draft ready");
    } catch {
      toast.error("Couldn't draft the reply — try again");
    } finally {
      setOLoading(false);
    }
  };

  if (!prospect) return null;
  const p = prospect;
  const hasThread = !!p.email_thread?.thread_id;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const saveProspectEmail = () => {
    const val = emailDraft.trim();
    if (!val.includes("@")) { toast.error("Enter a valid email"); return; }
    onPatch(p.id, { email: val }, "Email saved");
  };

  const sendOutreachEmail = async () => {
    setSendingEmail(true);    try {
      await sendEmail({ prospect_id: p.id, to: p.email, subject: emailSubject, body: emailBody });
      toast.success("Email sent from your Gmail");
      try { const { data } = await api.get(`/prospects/${p.id}`); onUpdate && onUpdate(data); } catch { /* ignore */ }
    } catch (e) {
      if (e?.response?.status === 428) { toast.message("Connect your Gmail to send from PIT"); connectGmail(); }
      else toast.error(e?.response?.data?.detail || "Could not send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const markContacted = () => {
    if (p.stage === "new_lead") onPatch(p.id, { stage: "follow_up_book" }, "Marked contacted — moved to Book-a-Call follow-up");
  };
  const markCallOutcome = (outcome) => {
    if (outcome === "completed") onPatch(p.id, { stage: "follow_up_close" }, "Marked attended — moved to Close follow-up");
    else onPatch(p.id, { stage: "follow_up_book", no_show: true }, "Marked no-show — back to Book-a-Call follow-up");
  };
  const clearNoShow = () => onPatch(p.id, { no_show: false }, "Cleared the no-show tag");

  const setFollowUp = (iso, msg) => onPatch(p.id, { next_follow_up_at: iso }, msg);
  const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); setFollowUp(d.toISOString(), `Follow-up set for ${n} days from today`); };
  const afterCall = (n) => { if (!p.call_time) return; const d = new Date(p.call_time); d.setDate(d.getDate() + n); setFollowUp(d.toISOString(), `Follow-up set ${n} days after the call`); };
  const onDatePick = (e) => { if (!e.target.value) return; setFollowUp(new Date(e.target.value + "T09:00:00").toISOString(), "Follow-up date updated"); };
  const markFollowUpSent = async () => {
    try {
      const { data } = await api.post(`/prospects/${p.id}/follow-up/sent`);
      onUpdate ? onUpdate(data) : onPatch(p.id, {}, null);
      toast.success("Follow-up logged — next touch scheduled");
    } catch {
      toast.error("Could not log follow-up");
    }
  };

  const del = async () => {
    await api.delete(`/prospects/${p.id}`);
    onDeleted(p.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="prospect-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="drawer-backdrop" />
      <div className="relative w-full max-w-md h-full overflow-y-auto bg-[#0f2442] border-l border-white/10 shadow-2xl p-6 fade-up">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="eyebrow mb-1">Prospect Intel</p>
            <h2 className="text-2xl font-extrabold text-white">{p.name || "—"}</h2>
            <p className="text-xs text-slate-500 font-mono-tech mt-0.5 uppercase tracking-wider">Source: {p.source}</p>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="drawer-tags">
              {p.contacted && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/15 text-sky-300 border border-sky-500/30">Contacted</span>}
              {p.had_call && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-500/15 text-teal-300 border border-teal-500/30">Had Call</span>}
              {p.no_show && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/15 text-rose-300 border border-rose-500/30">No-show</span>}
            </div>
            {["call_scheduled", "follow_up_book", "follow_up_close"].includes(p.stage) && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="call-outcome-controls">
                <p className="w-full text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Call outcome</p>
                {p.stage !== "follow_up_close" && (
                  <button
                    data-testid="mark-attended-btn"
                    onClick={() => markCallOutcome("completed")}
                    className="px-3 py-1.5 rounded-full bg-teal-500/15 border border-teal-500/40 text-teal-300 hover:bg-teal-500/25 text-xs font-semibold transition-colors"
                  >
                    Attended → Close flow
                  </button>
                )}
                {p.stage !== "follow_up_book" && (
                  <button
                    data-testid="mark-no-show-btn"
                    onClick={() => markCallOutcome("no_show")}
                    className="px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 text-xs font-semibold transition-colors"
                  >
                    No-show → Book flow
                  </button>
                )}
                {p.no_show && (
                  <button
                    data-testid="clear-no-show-btn"
                    onClick={clearNoShow}
                    className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-slate-300 hover:bg-white/10 text-xs font-semibold transition-colors"
                  >
                    Clear no-show tag
                  </button>
                )}
              </div>
            )}
          </div>
          <button data-testid="drawer-close-btn" onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2 text-sm mb-6">
          {p.email && (
            <p className="flex items-center gap-2 text-slate-300" data-testid="drawer-email"><Mail size={13} className="text-[#4690d9]" /> {p.email}</p>
          )}
          {p.phone && (
            <p className="flex items-center gap-2 text-slate-300" data-testid="drawer-phone"><Phone size={13} className="text-[#4690d9]" /> {p.phone}</p>
          )}
          {p.company && (
            <p className="flex items-center gap-2 text-slate-300" data-testid="drawer-company"><Building2 size={13} className="text-[#4690d9]" /> {p.company}</p>
          )}
        </div>

        <div className="glass-panel p-4 mb-4 space-y-4">
          <div>
            <p className="eyebrow text-[10px] mb-1.5">Stage</p>
            <StageSelect value={p.stage} onChange={(stage) => onPatch(p.id, { stage }, `Moved to ${stageMeta(stage).label}`)} testId="drawer-stage-select" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="eyebrow text-[10px] mb-1.5">Setup Fee (one-time)</p>
              <MoneyInput value={p.setup_fee} testId="drawer-setup-fee-input" onSave={(v) => onPatch(p.id, { setup_fee: v }, "Setup fee updated")} />
            </div>
            <div>
              <p className="eyebrow text-[10px] mb-1.5">Monthly</p>
              <MoneyInput value={p.monthly_value} suffix="/mo" testId="drawer-monthly-input" onSave={(v) => onPatch(p.id, { monthly_value: v }, "Monthly value updated")} />
            </div>
          </div>
        </div>

        <div className="glass-panel p-4 mb-4" data-testid="drawer-call-card">
          <p className="eyebrow text-[10px] mb-2 flex items-center gap-1.5">
            <CalendarCheck size={12} /> Calendly Call
          </p>
          {p.call_time ? (
            <div>
              <p className="text-white font-semibold font-mono-tech">
                {new Date(p.call_time).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </p>
              <p className={`text-xs mt-1 font-mono-tech uppercase tracking-wider ${p.call_status === "canceled" ? "text-rose-400" : "text-emerald-400"}`}>
                {p.call_status || "scheduled"}
              </p>
              {p.self_scheduled && (
                <p className="flex items-center gap-1.5 text-xs text-amber-400 mt-2" data-testid="self-booked-badge">
                  <Zap size={12} /> Self-booked immediately after application — no outreach needed
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No call scheduled yet. Send an outreach message below.</p>
          )}
        </div>

        <div className="glass-panel p-4 mb-4" data-testid="research-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="eyebrow text-[10px] flex items-center gap-1.5"><Sparkles size={12} /> Pre-call Research</p>
            <button
              data-testid="run-research-btn"
              onClick={runResearch}
              disabled={rStatus === "pending"}
              className="text-[10px] font-mono-tech uppercase tracking-wider text-[#9cc6f0] hover:text-white disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw size={11} className={rStatus === "pending" ? "animate-spin" : ""} /> {research && !research.error ? "Re-run" : "Run"}
            </button>
          </div>
          {rStatus === "pending" && <p className="text-xs text-slate-400">Researching the brand, tech stack &amp; person…</p>}
          {rStatus !== "pending" && !research && <p className="text-xs text-slate-500">No briefing yet. Click Run to generate one.</p>}
          {research && research.error && <p className="text-xs text-rose-400">Research failed — try Re-run.</p>}
          {research && !research.error && (
            <div className="space-y-3" data-testid="research-content">
              <p className="text-sm text-slate-200 leading-relaxed">{research.summary}</p>
              <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-xs">
                <div><span className="text-slate-500">Years:</span> <span className="text-slate-300">{research.years_in_business}</span></div>
                <div><span className="text-slate-500">Revenue:</span> <span className="text-slate-300">{research.monthly_revenue}</span></div>
                <div className="col-span-2"><span className="text-slate-500">Est. tech cost:</span> <span className="text-slate-300">{research.estimated_tech_cost}</span></div>
              </div>
              {research.tech_stack?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {research.tech_stack.map((t, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-[#4690d9]/15 border border-[#4690d9]/30 text-[#9cc6f0]">{t}</span>
                  ))}
                </div>
              )}
              {research.talking_points?.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">Talking points</p>
                  <ul className="space-y-1 list-disc list-inside">
                    {research.talking_points.map((t, i) => <li key={i} className="text-xs text-slate-300">{t}</li>)}
                  </ul>
                </div>
              )}
              {research.website && <a href={research.website} target="_blank" rel="noreferrer" className="text-[11px] text-[#4690d9] hover:underline">{research.website}</a>}
            </div>
          )}
        </div>

        <CallsPanel prospect={p} onRefresh={onUpdate} />

        {(p.stage === "follow_up_book" || p.stage === "follow_up_close") && (() => {
          const step = p.follow_up_step || 0;
          const complete = step >= 4;
          const next = p.next_follow_up_at ? new Date(p.next_follow_up_at) : null;
          const overdue = next && (() => { const t = new Date(); t.setHours(0, 0, 0, 0); const d = new Date(next); d.setHours(0, 0, 0, 0); return d < t; })();
          const dateVal = next ? new Date(next.getTime() - next.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : "";
          return (
            <div className="glass-panel p-4 mb-4" data-testid="followup-panel">
              <p className="eyebrow text-[10px] mb-2 flex items-center gap-1.5"><Clock size={12} /> Follow-up cadence</p>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-200" data-testid="followup-step">{complete ? "Cadence complete" : `Touch ${step + 1} of 4`}</span>
                {next ? (
                  <span className={`text-xs font-mono-tech ${overdue ? "text-rose-400" : "text-amber-300"}`} data-testid="followup-next">
                    {overdue ? "Overdue · " : "Next · "}{next.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  </span>
                ) : (
                  <span className="text-xs font-mono-tech text-slate-500">Not scheduled</span>
                )}
              </div>
              <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1.5">Set next follow-up</p>
              <input
                type="date"
                data-testid="followup-date-input"
                value={dateVal}
                onChange={onDatePick}
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#4690d9] mb-2 [color-scheme:dark]"
              />
              <div className="flex flex-wrap gap-2">
                <button data-testid="followup-plus7-btn" onClick={() => plusDays(7)} className="px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:text-white text-xs font-semibold transition-colors">+7 days</button>
                <button data-testid="followup-plus14-btn" onClick={() => plusDays(14)} className="px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:text-white text-xs font-semibold transition-colors">+14 days</button>
                {p.call_time && (
                  <button data-testid="followup-after-call-btn" onClick={() => afterCall(10)} className="px-3 py-1.5 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-xs font-semibold transition-colors">+10 days after call</button>
                )}
              </div>
              {!complete && (
                <button
                  data-testid="followup-sent-btn"
                  onClick={markFollowUpSent}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold transition-colors"
                >
                  <CalendarCheck size={13} /> Mark follow-up sent · schedule next touch
                </button>
              )}
            </div>
          );
        })()}

        {["new_lead", "follow_up_book", "follow_up_close"].includes(p.stage) && (
          <div className="glass-panel p-4 mb-4" data-testid="outreach-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="eyebrow text-[10px] flex items-center gap-1.5"><Sparkles size={12} /> {otype === "close_deal" ? "AI Outreach — Close the deal" : "AI Outreach — Get them booked"}</p>
              {outreach && (
                <button
                  data-testid="regen-outreach-btn"
                  onClick={() => draftOutreach(true)}
                  disabled={oLoading}
                  className="text-[10px] font-mono-tech uppercase tracking-wider text-[#9cc6f0] hover:text-white disabled:opacity-50 flex items-center gap-1"
                >
                  <RefreshCw size={11} className={oLoading ? "animate-spin" : ""} /> Regenerate
                </button>
              )}
            </div>

            {oLoading && !outreach && <p className="text-xs text-slate-400">Drafting a personalized reply…</p>}

            {!oLoading && !outreach && (
              <button
                data-testid="draft-outreach-btn"
                onClick={() => draftOutreach(false)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-xs font-semibold transition-colors"
              >
                <PenLine size={13} /> Draft AI reply
              </button>
            )}

            {outreach && (
              <div className="space-y-3">
                <div className="rounded-md bg-black/30 p-3">
                  {hasThread ? (
                    <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-2">↳ Continues the existing email thread</p>
                  ) : (
                    <input
                      data-testid="outreach-subject-input"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Subject"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9] mb-2"
                    />
                  )}
                  <textarea
                    data-testid="outreach-email-body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={7}
                    className="w-full bg-black/40 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-[#4690d9] resize-y"
                  />
                  <div className="flex gap-2 mt-2">
                    {p.email ? (
                      <button
                        data-testid="send-email-btn"
                        onClick={sendOutreachEmail}
                        disabled={sendingEmail}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold transition-colors disabled:opacity-60"
                      >
                        {sendingEmail ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />} {sendingEmail ? "Sending…" : "Send email"}
                      </button>
                    ) : (
                      <div className="flex gap-2 flex-1" data-testid="drawer-add-email">
                        <input
                          data-testid="drawer-email-input"
                          value={emailDraft}
                          onChange={(e) => setEmailDraft(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveProspectEmail()}
                          placeholder="Add email to send…"
                          className="flex-1 bg-black/40 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]"
                        />
                        <button
                          data-testid="drawer-save-email"
                          onClick={saveProspectEmail}
                          className="px-3 py-1.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    )}
                    <button
                      data-testid="copy-email-btn"
                      onClick={() => copy(emailBody, "Email")}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:text-white text-xs font-semibold transition-colors"
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>
                <div className="rounded-md bg-black/30 p-3">
                  <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">SMS</p>
                  <p className="text-xs text-slate-300" data-testid="outreach-sms-body">{outreach.sms}</p>
                  <div className="flex gap-2 mt-2">
                    <a
                      data-testid="send-sms-btn"
                      href={`sms:${p.phone || ""}?&body=${encodeURIComponent(outreach.sms)}`}
                      onClick={markContacted}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#003b8d] hover:bg-[#0a4aa0] text-white text-xs font-semibold transition-colors"
                    >
                      <MessageSquare size={12} /> Open SMS
                    </a>
                    <button
                      data-testid="copy-sms-btn"
                      onClick={() => { copy(outreach.sms, "SMS"); markContacted(); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:text-white text-xs font-semibold transition-colors"
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {p.sent_emails?.length > 0 && (
          <div className="glass-panel p-4 mb-4" data-testid="sent-emails-panel">
            <p className="eyebrow text-[10px] mb-2 flex items-center gap-1.5"><Send size={12} /> Sent emails</p>
            <div className="space-y-2">
              {[...p.sent_emails].reverse().map((m) => (
                <div key={m.id} className="rounded-md bg-black/30 p-2.5" data-testid={`sent-email-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-white truncate">{m.subject}</p>
                    <span className="text-[10px] font-mono-tech text-slate-500 shrink-0">{new Date(m.sent_at).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                  </div>
                  <p className="text-[11px] text-slate-500">to {m.to} · sent by {m.sent_by}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {p.typeform_answers?.length > 0 && (
          <div className="glass-panel p-4 mb-4" data-testid="typeform-answers-panel">
            <p className="eyebrow text-[10px] mb-2">Application Details</p>
            <div className="space-y-2">
              {p.typeform_answers.map((qa, i) => (
                <div key={i}>
                  <p className="text-[11px] text-slate-500">{qa.question}</p>
                  <p className="text-sm text-slate-200">{String(qa.answer ?? "—")}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="glass-panel p-4 mb-4">
          <p className="eyebrow text-[10px] mb-2">Notes</p>
          <textarea
            data-testid="drawer-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (p.notes || "") && onPatch(p.id, { notes }, "Notes saved")}
            rows={3}
            placeholder="Add follow-up notes…"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9] resize-none"
          />
        </div>

        <button
          data-testid="delete-prospect-btn"
          onClick={del}
          className="flex items-center gap-2 text-xs text-rose-400/70 hover:text-rose-400 transition-colors"
        >
          <Trash2 size={13} /> Delete prospect
        </button>
      </div>
    </div>
  );
}
